import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { CheckCircle2, XCircle, RefreshCw, Play, ExternalLink, ArrowRight } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { getTravelPayoutsStatus } from "@/lib/travelpayouts-status.functions";
import { traceFlightRedirects, type TraceResult } from "@/lib/travelpayouts-trace.functions";

export const Route = createFileRoute("/_authenticated/admin/travelpayouts-status")({
  head: () => ({
    meta: [{ title: "TravelPayouts — Diagnóstico" }, { name: "robots", content: "noindex" }],
  }),
  component: Page,
});

function todayPlus(days: number) {
  return new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
}

function Page() {
  const fn = useServerFn(getTravelPayoutsStatus);
  const traceFn = useServerFn(traceFlightRedirects);
  const [origin, setOrigin] = useState("CUN");
  const [destination, setDestination] = useState("MEX");
  const [departDate, setDepartDate] = useState(todayPlus(21));
  const [returnDate, setReturnDate] = useState(todayPlus(28));
  const [traces, setTraces] = useState<Record<string, TraceResult | "loading">>({});

  const m = useMutation({
    mutationFn: (bypassCache: boolean) =>
      fn({ data: { origin, destination, departDate, returnDate, bypassCache } }),
  });

  const run = (bypass = false) => m.mutate(bypass);
  const data = m.data;

  const runTrace = async (url: string) => {
    setTraces((t) => ({ ...t, [url]: "loading" }));
    try {
      const r = await traceFn({ data: { url } });
      setTraces((t) => ({ ...t, [url]: r }));
    } catch (e) {
      setTraces((t) => ({
        ...t,
        [url]: {
          originalUrl: url,
          finalUrl: url,
          hops: [],
          totalDurationMs: 0,
          redirectCount: 0,
          appRedirectCount: 0,
          travelPayoutsRedirectCount: 0,
          providerRedirectCount: 0,
          error: e instanceof Error ? e.message : String(e),
        },
      }));
    }
  };

  const openFinalCheckout = async (url: string) => {
    const existing = traces[url];
    if (existing && existing !== "loading") {
      window.location.assign(existing.finalUrl);
      return;
    }
    setTraces((t) => ({ ...t, [url]: "loading" }));
    try {
      const r = await traceFn({ data: { url } });
      setTraces((t) => ({ ...t, [url]: r }));
      window.location.assign(r.finalUrl);
    } catch (e) {
      setTraces((t) => ({
        ...t,
        [url]: {
          originalUrl: url,
          finalUrl: url,
          hops: [],
          totalDurationMs: 0,
          redirectCount: 0,
          appRedirectCount: 0,
          travelPayoutsRedirectCount: 0,
          providerRedirectCount: 0,
          error: e instanceof Error ? e.message : String(e),
        },
      }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-3xl font-semibold">Diagnóstico TravelPayouts</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Verifica configuración, endpoint, parámetros, latencia y resultados reales devueltos por la API.
        </p>

        <section className="mt-6 grid gap-3 rounded-2xl border border-border bg-card p-5 sm:grid-cols-5">
          <FieldLabel label="Origen (IATA)">
            <input value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase())} maxLength={3} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm font-mono uppercase" />
          </FieldLabel>
          <FieldLabel label="Destino (IATA)">
            <input value={destination} onChange={(e) => setDestination(e.target.value.toUpperCase())} maxLength={3} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm font-mono uppercase" />
          </FieldLabel>
          <FieldLabel label="Salida">
            <input type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
          </FieldLabel>
          <FieldLabel label="Regreso">
            <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
          </FieldLabel>
          <div className="flex items-end gap-2">
            <button onClick={() => run(false)} disabled={m.isPending} className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              <Play className="h-4 w-4" /> {m.isPending ? "Probando…" : "Probar"}
            </button>
            <button onClick={() => run(true)} disabled={m.isPending} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-border px-3 text-xs font-semibold" title="Limpiar caché y reintentar">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </section>

        {m.isError && (
          <p className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Error inesperado al invocar la función.
          </p>
        )}

        {data && (
          <>
            <section className="mt-6 grid gap-3 sm:grid-cols-2">
              <ConfigCard ok={data.config.tokenConfigured} title="API Token" value={data.config.tokenPreview ?? "No configurado"} />
              <ConfigCard ok={data.config.markerConfigured} title="Marker (afiliado)" value={data.config.markerPreview ?? "No configurado"} />
            </section>

            <section className="mt-6 rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-xl font-semibold">Respuesta</h2>
              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                <Row k="Endpoint" v={data.endpoint} mono />
                <Row k="HTTP Status" v={String(data.httpStatus)} mono />
                <Row k="Latencia total" v={`${data.durationMs} ms`} mono />
                <Row k="Caché" v={data.cacheHit ? "hit" : "miss"} />
                <Row k="Resultados recibidos" v={String(data.upstreamCount)} highlight />
                <Row k="Resultados de muestra (UI)" v={String(data.offersSample.length)} />
                <Row k="Cadena de fallback" v={data.fallbackChain} mono />
              </dl>

              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Parámetros enviados</h3>
                <pre className="mt-1 overflow-x-auto rounded-lg bg-muted p-3 text-[11px]">{JSON.stringify(data.params, null, 2)}</pre>
              </div>

              {data.error && (
                <p className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {data.error}
                </p>
              )}
            </section>

            <section className="mt-6 rounded-2xl border border-border bg-card p-5">
              <h2 className="font-display text-xl font-semibold">
                Resultados y trazado de redirección ({data.offersSample.length} de {data.upstreamCount})
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                "Abrir checkout final" resuelve primero las redirecciones en servidor y navega directo al destino final.
                Usa "Trazar redirecciones" para auditar los saltos.
              </p>
              {data.offersSample.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Sin resultados para esta búsqueda. Verifica fechas o ruta.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {data.offersSample.map((o, i) => {
                    const trace = traces[o.deeplink];
                    return (
                      <li key={i} className="space-y-2 py-3 text-sm">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                          <span className="font-mono font-semibold">{o.airline}</span>
                          <span>${o.price.toLocaleString("es-MX")} {o.currency}</span>
                          <span className="text-muted-foreground">{o.transfers === 0 ? "Directo" : `${o.transfers} esc.`}</span>
                          <span className="text-muted-foreground">{o.durationMinutes ?? "—"} min</span>
                          <span className="font-mono text-xs text-muted-foreground">{o.departAt ?? "—"}</span>
                          <span className="font-mono text-xs text-muted-foreground">{o.returnAt ?? "—"}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openFinalCheckout(o.deeplink)}
                            disabled={trace === "loading"}
                            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                          >
                            {trace === "loading" ? "Resolviendo…" : "Abrir checkout final"} <ExternalLink className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => runTrace(o.deeplink)}
                            disabled={trace === "loading"}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold disabled:opacity-60"
                          >
                            {trace === "loading" ? "Trazando…" : "Trazar redirecciones"}
                          </button>
                          <code className="truncate text-[11px] text-muted-foreground" title={o.deeplink}>
                            {o.deeplink}
                          </code>
                        </div>
                        {trace && trace !== "loading" && (
                          <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs">
                            <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
                              <span><b>Redirecciones:</b> {trace.redirectCount}</span>
                              <span><b>Nuestra app:</b> {trace.appRedirectCount}</span>
                              <span><b>TravelPayouts:</b> {trace.travelPayoutsRedirectCount}</span>
                              <span><b>Proveedor:</b> {trace.providerRedirectCount}</span>
                              <span><b>Tiempo total:</b> {trace.totalDurationMs} ms</span>
                              <span><b>Saltos:</b> {trace.hops.length}</span>
                            </div>
                            <div className="mb-2">
                              <b>URL final:</b>{" "}
                              <a href={trace.finalUrl} target="_blank" rel="noopener nofollow noreferrer" className="break-all text-primary underline">
                                {trace.finalUrl}
                              </a>
                            </div>
                            <ol className="space-y-1">
                              {trace.hops.map((h) => (
                                <li key={h.step} className="flex items-start gap-2 font-mono text-[11px]">
                                  <span className="shrink-0 rounded bg-background px-1.5 py-0.5">{h.step}</span>
                                  <span className="shrink-0 rounded bg-background px-1.5 py-0.5">{h.status || "ERR"}</span>
                                  <span className="shrink-0 text-muted-foreground">{h.durationMs}ms</span>
                                  <span className="break-all">{h.url}</span>
                                  {h.location && (
                                    <span className="flex items-center gap-1 break-all text-primary">
                                      <ArrowRight className="h-3 w-3 shrink-0" /> {h.location}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ol>
                            {trace.error && (
                              <p className="mt-2 text-destructive">Error: {trace.error}</p>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {data.rawSample && (
              <section className="mt-6 rounded-2xl border border-dashed border-border bg-muted/30 p-5">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Respuesta cruda (cuando ≤1 resultado)</h2>
                <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-background p-3 text-[11px]">{data.rawSample}</pre>
              </section>
            )}

            <section className="mt-6 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
              <h2 className="font-display text-base font-semibold text-foreground">Auditoría del frontend</h2>
              <ul className="mt-2 space-y-1">
                <li>✅ No existe <code>slice(0, 1)</code> en <code>FlightResults.tsx</code> / <code>InlineFlightSearch.tsx</code>.</li>
                <li>✅ No existe <code>limit=1</code>; servidor envía <code>limit=100</code> por defecto.</li>
                <li>✅ Paginación progresiva: 20 visibles + botón "Cargar más".</li>
                <li>✅ Filtros por escalas / aerolínea / precio máximo aplicados sin descartar resultados.</li>
                <li>ℹ️ Si la API devuelve 0 para una fecha exacta, se reintenta con <code>YYYY-MM</code> y finalmente sin filtro de fecha (mostrando los precios cacheados más recientes en esa ruta).</li>
              </ul>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ConfigCard({ ok, title, value }: { ok: boolean; title: string; value: string }) {
  return (
    <div className={`rounded-2xl border p-4 ${ok ? "border-success/40 bg-success/5" : "border-destructive/40 bg-destructive/5"}`}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle2 className="h-5 w-5 text-success" /> : <XCircle className="h-5 w-5 text-destructive" />}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-1 font-mono text-xs text-muted-foreground">{value}</p>
    </div>
  );
}

function Row({ k, v, mono, highlight }: { k: string; v: string; mono?: boolean; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/40 py-1.5">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`${mono ? "font-mono" : ""} ${highlight ? "text-base font-semibold text-primary" : "text-foreground"}`}>{v}</dd>
    </div>
  );
}
